/* Pışpışla brand layer: motion engine + buy box.
   Vanilla JS (ES module), no dependencies. Every effect degrades to static content. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = window.matchMedia('(pointer: fine)').matches;

/* ---------- Money ---------- */
export function formatMoney(cents) {
  const value = Number(cents) / 100;
  const hasFraction = Math.round(value * 100) % 100 !== 0;
  return (
    new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(value) + ' TL'
  );
}

/* ---------- Kinetic text: split words, then reveal ---------- */
function splitWords(el) {
  if (el.dataset.ppSplitDone) return;
  el.dataset.ppSplitDone = '1';
  let i = 0;
  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const parts = child.textContent.split(/(\s+)/);
        const frag = document.createDocumentFragment();
        parts.forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
            return;
          }
          const w = document.createElement('span');
          w.className = 'pp-w';
          const inner = document.createElement('span');
          inner.textContent = part;
          w.style.setProperty('--i', i++);
          w.appendChild(inner);
          frag.appendChild(w);
        });
        child.replaceWith(frag);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        walk(child);
      }
    });
  };
  walk(el);
  el.classList.add('pp-split');
}

/* ---------- Reveal on scroll ---------- */
function initReveal(root = document) {
  root.querySelectorAll('[data-pp-split]').forEach(splitWords);
  const targets = root.querySelectorAll('[data-pp-reveal], [data-pp-split], [data-pp-inview]');
  if (!('IntersectionObserver' in window) || reduceMotion) {
    targets.forEach((t) => t.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    },
    { rootMargin: '0px 0px 35% 0px', threshold: 0 }
  );
  targets.forEach((t) => io.observe(t));
}

/* ---------- Battery: 100% → 1% loop ---------- */
function initBattery(root = document) {
  root.querySelectorAll('[data-pp-battery]').forEach((el) => {
    const pct = el.querySelector('.pp-batt__pct');
    const from = Number(el.dataset.from || 34);
    const set = (v) => {
      el.style.setProperty('--pp-batt', v);
      if (pct) pct.textContent = `%${v}`;
      el.classList.toggle('is-low', v <= 5);
    };
    if (reduceMotion) {
      set(1);
      return;
    }
    let v = from;
    let hold = 0;
    set(v);
    setInterval(() => {
      if (document.hidden) return;
      if (v > 1) {
        v -= 1;
        set(v);
      } else if (++hold > 24) {
        hold = 0;
        v = from;
        set(v);
      }
    }, 120);
  });
}

/* ---------- Live clock "03:10" kicker (static story time, ticking seconds) ---------- */
function initClock(root = document) {
  root.querySelectorAll('[data-pp-clock]').forEach((el) => {
    if (reduceMotion) return;
    let m = 10;
    setInterval(() => {
      if (document.hidden) return;
      m = m >= 14 ? 10 : m + 1;
      el.textContent = `03:${String(m).padStart(2, '0')}`;
    }, 4000);
  });
}

/* ---------- Spotlight cards (pointer glow) ---------- */
function initSpotlight(root = document) {
  if (!finePointer || reduceMotion) return;
  root.querySelectorAll('.pp-tile').forEach((tile) => {
    tile.addEventListener('pointermove', (e) => {
      const r = tile.getBoundingClientRect();
      tile.style.setProperty('--mx', `${e.clientX - r.left}px`);
      tile.style.setProperty('--my', `${e.clientY - r.top}px`);
    });
  });
}

/* ---------- Gallery ---------- */
function initGallery(gal) {
  const track = gal.querySelector('.pp-gal__track');
  if (!track) return;
  const slides = [...track.children];
  const dots = [...gal.querySelectorAll('.pp-gal__dot')];
  const thumbs = [...gal.querySelectorAll('.pp-gal__thumb')];
  let current = 0;

  const mark = (i) => {
    current = i;
    dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
    thumbs.forEach((t, k) => t.setAttribute('aria-current', String(k === i)));
    slides.forEach((s, k) => {
      const v = s.querySelector('video');
      if (!v) return;
      if (k === i) v.play().catch(() => {});
      else v.pause();
    });
  };

  const go = (i) => {
    const n = (i + slides.length) % slides.length;
    track.scrollTo({ left: slides[n].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    mark(n);
  };

  gal.goTo = go;
  dots.forEach((d, i) => d.addEventListener('click', () => go(i)));
  thumbs.forEach((t, i) => t.addEventListener('click', () => go(i)));
  gal.querySelector('.pp-gal__nav--prev')?.addEventListener('click', () => go(current - 1));
  gal.querySelector('.pp-gal__nav--next')?.addEventListener('click', () => go(current + 1));

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.6) mark(slides.indexOf(e.target));
        });
      },
      { root: track, threshold: [0.6] }
    );
    slides.forEach((s) => io.observe(s));
  }
}

/* ---------- Delivery estimate (business days, Istanbul time) ---------- */
function addBusinessDays(date, days) {
  const d = new Date(date);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) added++;
  }
  return d;
}

function initDelivery(root = document) {
  root.querySelectorAll('[data-pp-delivery]').forEach((el) => {
    const min = Number(el.dataset.min || 3);
    const max = Number(el.dataset.max || 6);
    const now = new Date();
    const a = addBusinessDays(now, min);
    const b = addBusinessDays(now, max);
    const fmt = new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'long' });
    const sameMonth = a.getMonth() === b.getMonth();
    const txt = sameMonth
      ? `${a.getDate()}–${fmt.format(b)}`
      : `${fmt.format(a)} – ${fmt.format(b)}`;
    el.querySelectorAll('[data-pp-delivery-out]').forEach((out) => (out.textContent = txt));
  });
}

/* ---------- Buy box ---------- */
function initBuyBox(box) {
  const dataEl = box.querySelector('script[data-pp-product]');
  if (!dataEl) return;
  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }
  const radios = [...box.querySelectorAll('input[name="pp-bundle"]')];
  const addon = box.querySelector('input[data-pp-addon]');
  const priceEls = document.querySelectorAll(`[data-pp-price="${data.section}"]`);
  const ctas = document.querySelectorAll(`[data-pp-cta="${data.section}"]`);
  const gal = box.querySelector('.pp-gal');
  const esVariant = box.querySelector('[data-pp-es-variant]');
  // v7 (03.11): renk düğmeleri. Paket radyosu ilk rengin varyantını taşır; seçilen renkteki eşi burada bulunur.
  const colors = [...box.querySelectorAll('input[name="sm-renk"]')];
  const ci = Number.isInteger(data.color_index) ? data.color_index : -1;
  const colorName = box.querySelector('[data-sm-renk-ad]');
  const kutuImgs = [...box.querySelectorAll('[data-sm-renk-img]')];
  const stickyImg = box.querySelector('.pp-sticky__img');
  const sticky0 = stickyImg ? { src: stickyImg.getAttribute('src'), srcset: stickyImg.getAttribute('srcset') } : null;

  const selected = () => {
    const r = radios.find((x) => x.checked) || radios[0];
    let v = data.variants.find((x) => String(x.id) === r?.value) || data.variants[0];
    const c = colors.find((x) => x.checked);
    if (v && c && ci >= 0 && Array.isArray(v.options)) {
      const want = v.options.map((o, i) => (i === ci ? c.value : o));
      v = data.variants.find((x) => Array.isArray(x.options) && x.options.every((o, i) => o === want[i])) || v;
    }
    return v;
  };

  const showColor = (v, jump) => {
    const c = colors.find((x) => x.checked);
    if (!c) return;
    if (colorName) colorName.textContent = c.value;
    if (c.dataset.img) kutuImgs.forEach((im) => (im.src = c.dataset.img));
    if (stickyImg && sticky0) {
      if (v.img) {
        stickyImg.removeAttribute('srcset');
        stickyImg.src = v.img;
      } else {
        stickyImg.src = sticky0.src;
        if (sticky0.srcset) stickyImg.setAttribute('srcset', sticky0.srcset);
      }
    }
    if (jump && gal && gal.goTo) gal.goTo(Number.isInteger(v.media_index) && v.media_index >= 0 ? v.media_index : 0);
  };

  const total = () => {
    const v = selected();
    let t = v ? v.price : 0;
    if (addon && addon.checked) t += Number(addon.dataset.price || 0);
    return t;
  };

  const render = (initial = false) => {
    const v = selected();
    if (!v) return;
    const t = formatMoney(total());
    if (esVariant) esVariant.value = v.id;
    priceEls.forEach((el) => (el.textContent = t));
    ctas.forEach((b) => {
      const lbl = b.querySelector('[data-pp-cta-total]');
      if (lbl) lbl.textContent = t;
      b.disabled = !v.available;
    });
    if (!initial && gal && gal.goTo && Number.isInteger(v.media_index) && v.media_index >= 0) gal.goTo(v.media_index);
  };

  radios.forEach((r) => r.addEventListener('change', () => render()));
  addon?.addEventListener('change', () => render());
  colors.forEach((c) => c.addEventListener('change', () => {
    render(true);
    showColor(selected(), true);
  }));

  const buy = async (btn) => {
    const v = selected();
    if (!v || !v.available) return;
    btn.setAttribute('aria-busy', 'true');
    const items = [{ id: v.id, quantity: 1 }];
    if (addon && addon.checked && addon.value) items.push({ id: Number(addon.value), quantity: 1 });

    // EasySell COD form: it orders the page product (variant from the hidden input) plus the
    // cart contents, so the cart holds only the gift add-on when it is ticked.
    if (typeof window.openTysloForm === 'function') {
      if (esVariant) esVariant.value = v.id;
      try {
        await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
        if (items.length > 1) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items.slice(1) }),
          });
        }
      } catch (e) {
        // The form still opens with the main product.
      }
      btn.removeAttribute('aria-busy');
      window.openTysloForm();
      return;
    }

    try {
      await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('add failed');
      window.location.href = '/checkout';
    } catch (e) {
      // Cart permalink fallback goes straight to checkout with the selected items.
      window.location.href = `/cart/${items.map((i) => `${i.id}:${i.quantity}`).join(',')}`;
    }
  };

  ctas.forEach((b) => b.addEventListener('click', (e) => {
    e.preventDefault();
    buy(b);
  }));

  // ?renk=pembe (reklam bağlantısı) rengi önceden seçer; ?variant= ile gelen renk Liquid'de işaretlenir.
  const wanted = new URLSearchParams(location.search).get('renk');
  if (wanted) {
    const w = wanted.toLocaleLowerCase('tr');
    const c = colors.find((x) => x.value.toLocaleLowerCase('tr') === w);
    if (c) c.checked = true;
  }
  render(true);
  if (colors.length && colors.findIndex((x) => x.checked) > 0) showColor(selected(), true);
}

/* ---------- Sticky bar: shows once the main CTA leaves the viewport ---------- */
function initSticky() {
  const bar = document.querySelector('[data-pp-sticky]');
  const anchor = document.querySelector('[data-pp-sticky-anchor]');
  if (!bar || !anchor) return;
  // The bar is rendered inside the buy section's .pp-wrap (position: relative; z-index: 1), which
  // traps it in that stacking context, so later sections painted over it. Lift it to <body>;
  // its click and price listeners stay attached to the moved nodes.
  document.querySelectorAll('body > [data-pp-sticky]').forEach((old) => old !== bar && old.remove());
  if (bar.parentElement !== document.body) document.body.appendChild(bar);
  const footer = document.querySelector('.pp-foot');
  // Measured on scroll rather than with IntersectionObserver: a fast fling can jump the
  // anchor from below the viewport to above it without ever intersecting.
  let queued = false;
  const update = () => {
    queued = false;
    const pastAnchor = anchor.getBoundingClientRect().bottom < 0;
    const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
    bar.classList.toggle('is-on', pastAnchor && !footerVisible);
  };
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };
  // capture: on desktop Horizon scrolls .page-wrapper, whose scroll events never reach window.
  document.addEventListener('scroll', queue, { passive: true, capture: true });
  window.addEventListener('resize', queue, { passive: true });
  update();
}

/* ---------- Smooth details open/close ---------- */
function initDetails(root = document) {
  if (reduceMotion) return;
  root.querySelectorAll('.pp-faq details, .pp-acc details').forEach((d) => {
    const summary = d.querySelector('summary');
    const body = d.querySelector('.pp-faq__body, .pp-acc__body');
    if (!summary || !body) return;
    summary.addEventListener('click', (e) => {
      e.preventDefault();
      if (d.open) {
        const h = body.offsetHeight;
        body.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], {
          duration: 260,
          easing: 'cubic-bezier(.22,1,.36,1)',
        }).onfinish = () => d.removeAttribute('open');
      } else {
        d.setAttribute('open', '');
        const h = body.offsetHeight;
        body.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], {
          duration: 380,
          easing: 'cubic-bezier(.22,1,.36,1)',
        });
      }
    });
  });
}

/* ---------- Scroll-to-buy links ---------- */
function initScrollLinks() {
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href*="#pp-buy"]');
    if (!a) return;
    // "/#pp-buy" from the cart drawer: handle it here only when the buy box is on this page.
    const url = new URL(a.href, location.href);
    if (url.pathname !== location.pathname) return;
    const target = document.querySelector(url.hash);
    if (!target) return;
    e.preventDefault();
    const drawer = a.closest('theme-drawer');
    const go = () => target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
      setTimeout(go, 350); // wait for the drawer to release its scroll lock
    } else {
      go();
    }
    const gift = a.dataset.ppSelect;
    if (gift) {
      const r = document.querySelector(`input[name="pp-bundle"][data-key="${gift}"]`);
      if (r) {
        r.checked = true;
        r.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (a.dataset.ppAddon) {
      const ad = document.querySelector('input[data-pp-addon]');
      if (ad && !ad.checked) {
        ad.checked = true;
        ad.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });
}

/* ---------- Videos: play only while visible ---------- */
function initVideos(root = document) {
  const vids = root.querySelectorAll('video[data-pp-autoplay]');
  if (!vids.length) return;
  if (reduceMotion) {
    vids.forEach((v) => v.removeAttribute('autoplay'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) e.target.play().catch(() => {});
      else e.target.pause();
    });
  }, { threshold: 0.2 });
  vids.forEach((v) => io.observe(v));
}

function boot(root = document) {
  initReveal(root);
  initBattery(root);
  initClock(root);
  initSpotlight(root);
  initDelivery(root);
  initDetails(root);
  initVideos(root);
  root.querySelectorAll('.pp-gal').forEach(initGallery);
  root.querySelectorAll('[data-pp-buybox]').forEach(initBuyBox);
}

boot();
initSticky();
initScrollLinks();

// Where the page scrolls: the window on phones, but on desktop (990px+) Horizon locks html/body
// and scrolls .page-wrapper instead, so window.scrollTo alone does nothing there.
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'instant' });
  const pw = document.querySelector('.page-wrapper');
  if (pw && pw.scrollTop !== 0) pw.scrollTo({ top: 0, behavior: 'instant' });
};
const isPageTop = () => window.scrollY === 0 && !(document.querySelector('.page-wrapper')?.scrollTop > 0);

// In-page links such as the cart drawer's "/#pp-buy" leave the hash in the address bar, and a
// later reload would jump back to the buy box. Keep the jump, then drop the hash.
const PP_HASH = /^#(pp-|MainContent$)/;
const dropHash = () => {
  if (PP_HASH.test(location.hash)) history.replaceState(history.state, '', location.pathname + location.search);
};
window.addEventListener('hashchange', () => setTimeout(dropHash, 60));

(() => {
  const nav = performance.getEntriesByType('navigation')[0];
  if (!nav || nav.type !== 'reload') {
    // Arriving from an ad or another page with #pp-buy: let the browser jump, then clean up.
    window.addEventListener('load', dropHash);
    return;
  }
  if (location.hash && !PP_HASH.test(location.hash)) return;
  // A reload starts at the top. The browser may restore the old position (or a #pp-buy jump)
  // around the load event, so re-apply until the visitor scrolls on their own.
  dropHash();
  let userMoved = false;
  const mark = () => (userMoved = true);
  ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((t) =>
    window.addEventListener(t, mark, { once: true, passive: true, capture: true })
  );
  const toTop = () => {
    if (!userMoved && !isPageTop()) scrollToTop();
  };
  toTop();
  requestAnimationFrame(toTop);
  window.addEventListener('load', () => {
    toTop();
    setTimeout(toTop, 150);
    setTimeout(toTop, 500);
    setTimeout(toTop, 1200);
  });
})();

// Theme editor: re-run when a section is re-rendered.
document.addEventListener('shopify:section:load', (e) => boot(e.target));
